import React, { useState } from 'react';
import {
  Badge, Dropdown, Menu, MenuButton, MenuItem, Typography, Box, Button, Chip,
} from '@mui/joy';
import {
  Bell, ChatCircle, ClipboardText, Briefcase, Info,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { useNotifications, useUnreadNotificationCount, useNotificationMutations } from '../hooks/useNotifications';
import type { Notification } from '../hooks/useNotifications';

const iconFor = (type: Notification['notification_type']) => {
  switch (type) {
    case 'mention': case 'comment': return <ChatCircle size={18} />;
    case 'task': case 'task_assigned': case 'task_completed': return <ClipboardText size={18} />;
    case 'job': return <Briefcase size={18} />;
    default: return <Info size={18} />;
  }
};

export const NotificationIcon: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data: notifications = [], isLoading, isError, refetch } = useNotifications(10, open);
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const { markAsRead, markAllAsRead } = useNotificationMutations();

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead.mutate(notification.id, { onError: () => toast.error('Could not mark this notification as read.') });
    }
    if (notification.related_url?.startsWith('/')) {
      navigate(notification.related_url);
    } else if (notification.related_url) {
      window.location.href = notification.related_url;
    } else {
      navigate('/settings/notifications');
    }
    setOpen(false);
  };

  return (
    <Dropdown open={open} onOpenChange={(_, isOpen) => setOpen(isOpen)}>
      <MenuButton aria-label="Notifications" variant="plain" sx={{
        color: 'white', minHeight: 40, minWidth: 40, borderRadius: '50%',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
      }}>
        <Badge badgeContent={unreadCount} invisible={unreadCount === 0} color="danger" size="sm">
          <Bell color="white" />
        </Badge>
      </MenuButton>
      <Menu placement="bottom-end" aria-label="Recent notifications" sx={{
        width: 'min(420px, calc(100vw - 24px))', maxHeight: 'min(600px, calc(100dvh - 110px))',
        overflowY: 'auto', zIndex: 1200, p: 0, boxShadow: 'lg', bgcolor: 'background.surface',
        scrollPaddingTop: '80px', scrollPaddingBottom: '52px',
      }}>
        <Box component="li" role="presentation" sx={{
          px: 2, py: 1.5, position: 'sticky', top: 0, zIndex: 1,
          bgcolor: 'background.surface', borderBottom: '1px solid', borderColor: 'divider',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Typography level="title-md">Notifications</Typography>
            <Button size="sm" variant="plain" disabled={unreadCount === 0 || markAllAsRead.isPending}
              onClick={() => markAllAsRead.mutate(undefined, {
                onSuccess: () => toast.success('All notifications marked as read'),
                onError: () => toast.error('Failed to mark notifications as read'),
              })}>
              Mark all as read
            </Button>
          </Box>
          <Typography level="body-xs">10 most recent · {unreadCount} unread</Typography>
        </Box>
        {isLoading ? (
          <Box component="li" role="presentation" sx={{ p: 3 }}><Typography role="status">Loading notifications…</Typography></Box>
        ) : isError ? (
          <Box component="li" role="presentation" sx={{ p: 2 }}>
            <Typography level="body-sm" role="alert">Could not load notifications.</Typography>
            <Button size="sm" variant="plain" onClick={() => refetch()}>Try again</Button>
          </Box>
        ) : notifications.length === 0 ? (
          <Box component="li" role="presentation" sx={{ p: 3 }}><Typography level="body-sm">No notifications yet.</Typography></Box>
        ) : notifications.slice(0, 10).map(notification => (
          <MenuItem key={notification.id} onClick={() => handleNotificationClick(notification)} sx={{
            alignItems: 'flex-start', gap: 1.5, px: 2, py: 1.5, whiteSpace: 'normal',
            borderBottom: '1px solid', borderColor: 'divider',
            bgcolor: notification.is_read ? 'transparent' : 'background.level1',
          }}>
            <Box sx={{ pt: 0.25, color: notification.is_read ? 'text.tertiary' : 'primary.plainColor' }}>{iconFor(notification.notification_type)}</Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography level="title-sm" sx={{ fontWeight: notification.is_read ? 500 : 700, overflowWrap: 'anywhere' }}>
                {notification.title}
              </Typography>
              <Typography level="body-sm" sx={{ mt: 0.5, overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {notification.body}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                <Typography level="body-xs">{formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}</Typography>
                {!notification.is_read && <Chip size="sm" variant="soft" color="primary">Unread</Chip>}
              </Box>
            </Box>
          </MenuItem>
        ))}
        <MenuItem onClick={() => { navigate('/settings/notifications'); setOpen(false); }} sx={{
          position: 'sticky', bottom: 0, bgcolor: 'background.surface', zIndex: 1,
          justifyContent: 'center', py: 1.5, borderTop: '1px solid', borderColor: 'divider',
        }}>
          <Typography level="title-sm" color="primary">View all notifications</Typography>
        </MenuItem>
      </Menu>
    </Dropdown>
  );
};
